import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { safeLocalStorage } from '@/lib/safeStorage'

export type AnswerWeight = 0 | 1 | 2 | 3

export interface UserLocation {
  type: 'gps' | 'search'
  latitude: number
  longitude: number
  addressText: string
}

interface ChecklistState {
  answers: Record<string, AnswerWeight>
  step: 1 | 2 | 3
  completedAt: string | null
  targetFacilityIds: number[]
  userLocation: UserLocation | null
  /** 시설 상세에서 체크리스트 진입 시 — 결과 페이지에서 이 시설의 단일 자동 검증 카드를 상단에 노출하기 위한 컨텍스트 */
  entryFacilityId: number | null
}

interface ChecklistActions {
  setAnswer: (code: string, weight: AnswerWeight) => void
  setStep: (step: 1 | 2 | 3) => void
  setTargetFacilityIds: (ids: number[]) => void
  setCompletedAt: (at: string | null) => void
  setUserLocation: (loc: UserLocation | null) => void
  setEntryFacilityId: (id: number | null) => void
  reset: () => void
  hydrateFromServer: (payload: {
    answers: Record<string, number>
    completedAt: string | null
    targetFacilityIds: number[]
  }) => void
}

const initialState: ChecklistState = {
  answers: {},
  step: 1,
  completedAt: null,
  targetFacilityIds: [],
  userLocation: null,
  entryFacilityId: null,
}

export const useChecklistStore = create<ChecklistState & ChecklistActions>()(
  persist(
    (set) => ({
      ...initialState,

      setAnswer: (code, weight) =>
        set((state) => ({
          answers: { ...state.answers, [code]: weight },
        })),

      setStep: (step) => set({ step }),

      setTargetFacilityIds: (ids) => set({ targetFacilityIds: ids }),

      setCompletedAt: (at) => set({ completedAt: at }),

      setUserLocation: (loc) => set({ userLocation: loc }),

      setEntryFacilityId: (id) => set({ entryFacilityId: id }),

      // 응답/단계만 초기화 — userLocation 은 사용자가 한 번 입력한 후 유지 (다시 입력 마찰 회피).
      // 위치 변경은 setUserLocation 으로 명시적으로만 가능.
      // entryFacilityId 는 reset 시 비움 — 시설 컨텍스트는 1회성.
      reset: () =>
        set((state) => ({
          ...initialState,
          userLocation: state.userLocation,
        })),

      hydrateFromServer: ({ answers, completedAt, targetFacilityIds }) =>
        set({
          answers: Object.fromEntries(
            Object.entries(answers).map(([code, w]) => [code, clampWeight(w)])
          ),
          completedAt,
          targetFacilityIds: targetFacilityIds || [],
        }),
    }),
    {
      name: 'cb_checklist',
      storage: createJSONStorage(() => safeLocalStorage),
    }
  )
)

function clampWeight(w: number): AnswerWeight {
  if (w === 1 || w === 2 || w === 3) return w
  return 0
}
